/**
 * useBreakpoint Hook
 *
 * Tracks the current Tailwind breakpoint ('sm' | 'md' | 'lg' | 'xl' | '2xl').
 * Useful for responsive layouts that need more granular control than just mobile/desktop.
 *
 * @example
 * ```tsx
 * const breakpoint = useBreakpoint();
 *
 * return (
 *   <div>
 *     {breakpoint === 'sm' && <SmallLayout />}
 *     {breakpoint === 'md' && <MediumLayout />}
 *     {['lg', 'xl', '2xl'].includes(breakpoint) && <LargeLayout />}
 *   </div>
 * );
 * ```
 */

import { useState, useEffect } from 'react';

/**
 * Tailwind breakpoint definitions (matches tailwind.config.js)
 */
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

/**
 * Breakpoint type
 */
export type Breakpoint = keyof typeof BREAKPOINTS | 'xs';

/**
 * Get current breakpoint from window width
 *
 * @param width - Window width in pixels
 * @returns Breakpoint name
 */
const getBreakpoint = (width: number): Breakpoint => {
  if (width >= BREAKPOINTS['2xl']) return '2xl';
  if (width >= BREAKPOINTS.xl) return 'xl';
  if (width >= BREAKPOINTS.lg) return 'lg';
  if (width >= BREAKPOINTS.md) return 'md';
  if (width >= BREAKPOINTS.sm) return 'sm';
  return 'xs';
};

/**
 * Hook to track current Tailwind breakpoint
 *
 * @returns Current breakpoint ('xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl')
 */
export const useBreakpoint = (): Breakpoint => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => {
    // Initialize with current state (SSR-safe)
    if (typeof window === 'undefined') {
      return 'lg'; // Default for SSR
    }
    return getBreakpoint(window.innerWidth);
  });

  useEffect(() => {
    let timeoutId: number | undefined;

    // Handler for window resize (debounced)
    const handleResize = () => {
      // Clear existing timeout
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      // Debounce resize events (100ms)
      timeoutId = window.setTimeout(() => {
        const newBreakpoint = getBreakpoint(window.innerWidth);
        if (newBreakpoint !== breakpoint) {
          setBreakpoint(newBreakpoint);
        }
      }, 100);
    };

    // Add listener
    window.addEventListener('resize', handleResize);

    // Set initial state
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [breakpoint]);

  return breakpoint;
};

/**
 * Helper functions for breakpoint comparisons
 */
export const breakpointHelpers = {
  /**
   * Check if current breakpoint is at or above target
   */
  isAtLeast: (current: Breakpoint, target: Breakpoint): boolean => {
    const order: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    return order.indexOf(current) >= order.indexOf(target);
  },

  /**
   * Check if current breakpoint is below target
   */
  isBelow: (current: Breakpoint, target: Breakpoint): boolean => {
    const order: Breakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
    return order.indexOf(current) < order.indexOf(target);
  },

  /**
   * Check if current breakpoint is mobile (xs or sm)
   */
  isMobile: (current: Breakpoint): boolean => {
    return current === 'xs' || current === 'sm';
  },

  /**
   * Check if current breakpoint is tablet (md)
   */
  isTablet: (current: Breakpoint): boolean => {
    return current === 'md';
  },

  /**
   * Check if current breakpoint is desktop (lg or above)
   */
  isDesktop: (current: Breakpoint): boolean => {
    return current === 'lg' || current === 'xl' || current === '2xl';
  },
};
